import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Linkedin, Globe, Briefcase, GraduationCap, Award, Star } from 'lucide-react';
import resumeAPI from '../../services/api';
import ResumeBrandingWrapper from './ResumeBrandingWrapper';
import { getFontStyle, getHeaderFontStyle } from '../../utils/fontSizeHelper';

const StructuredFallback = ({ resume }) => {
  const highlightColor = resume?.highlight_color || '#3b82f6';
  const experiences = resume?.experience || [];
  const education = resume?.education || [];
  const skills = resume?.skills || [];
  const coreSkills = resume?.core_skills || [];
  const certifications = resume?.certifications || [];
  const languages = resume?.languages || [];
  const awards = resume?.awards || [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-4 pb-4 border-b-2" style={{ borderColor: highlightColor }}>
        <h1 className="text-2xl font-bold text-gray-900">{resume?.full_name || resume?.title || 'Untitled'}</h1>
        {resume?.headline && <p className="text-lg text-gray-600 mt-1">{resume.headline}</p>}
        {resume?.credentials && <p className="text-sm text-gray-500">{resume.credentials}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
          {resume?.contact_email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{resume.contact_email}</span>}
          {resume?.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{resume.contact_phone}</span>}
          {resume?.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{resume.location}</span>}
          {resume?.linkedin_url && <span className="flex items-center gap-1"><Linkedin className="w-3.5 h-3.5" />LinkedIn</span>}
          {resume?.website_url && <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{resume.website_url}</span>}
        </div>
      </div>

      {/* Professional Summary */}
      {resume?.professional_summary && (
        <div className="mb-4" style={getFontStyle(resume, 'summary')}>
          <h2 className="text-lg font-bold mb-1" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'summary') }}>Professional Summary</h2>
          <p className="text-sm text-gray-700 whitespace-pre-line">{resume.professional_summary}</p>
        </div>
      )}

      {/* Core Skills */}
      {coreSkills.length > 0 && (
        <div className="mb-4" style={getFontStyle(resume, 'core_skills')}>
          <h2 className="text-lg font-bold mb-1 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'core_skills') }}><Star className="w-4 h-4" />Core Skills</h2>
          <div className="grid grid-cols-2 gap-1">
            {coreSkills.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-1 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: highlightColor }}></span>
                {s.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Experience */}
      {experiences.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg font-bold mb-2 flex items-center gap-2" style={{ color: highlightColor }}><Briefcase className="w-4 h-4" />Experience</h2>
          {experiences.map((exp, i) => (
            <div key={i} className="mb-3 pl-3 border-l-2" style={{ borderColor: `${highlightColor}60` }}>
              <div className="font-semibold text-gray-900">{exp.title}</div>
              {exp.company && <div className="text-gray-700">{exp.company}</div>}
              <div className="text-xs text-gray-500">
                {[exp.start_month, exp.start_year].filter(Boolean).join(' ')}
                {(exp.start_month || exp.start_year) && ' - '}
                {exp.is_current ? 'Present' : [exp.end_month, exp.end_year].filter(Boolean).join(' ')}
                {exp.location && ` | ${exp.location}`}
              </div>
              {exp.description && (() => {
                const lines = exp.description.split('\n').filter(l => l.trim());
                const bulletRegex = /^[•\-\*▪▸►◆○●·⬥]\s*/;
                const hasBullets = lines.some(l => bulletRegex.test(l.trim()));
                if (hasBullets) {
                  return (
                    <ul className="mt-1 space-y-1.5 list-none pl-0">
                      {lines.map((line, li) => {
                        const trimmed = line.trim();
                        const text = trimmed.replace(bulletRegex, '');
                        return (
                          <li key={li} className="text-sm text-gray-700 flex items-start">
                            <span className="mr-2 flex-shrink-0" style={{ color: highlightColor }}>•</span>
                            <span>{text || trimmed}</span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                }
                return <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{exp.description}</p>;
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {education.length > 0 && (
        <div className="mb-4" style={getFontStyle(resume, 'education')}>
          <h2 className="text-lg font-bold mb-2 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'education') }}><GraduationCap className="w-4 h-4" />Education</h2>
          {education.map((edu, i) => (
            <div key={i} className="mb-2 pl-3 border-l-2" style={{ borderColor: `${highlightColor}60` }}>
              <div className="font-semibold text-gray-900">{edu.degree}</div>
              {edu.institution && <div className="text-gray-700">{edu.institution}</div>}
              <div className="text-xs text-gray-500">
                {edu.start_year && `${edu.start_year} - `}{edu.end_year || ''}
              </div>
              {edu.description && <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{edu.description}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div className="mb-4" style={getFontStyle(resume, 'skills')}>
          <h2 className="text-lg font-bold mb-1" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'skills') }}>Skills</h2>
          <p className="text-sm text-gray-700">{skills.join(', ')}</p>
        </div>
      )}

      {/* Certifications */}
      {certifications.length > 0 && (
        <div className="mb-4" style={getFontStyle(resume, 'certifications')}>
          <h2 className="text-lg font-bold mb-1 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'certifications') }}><Award className="w-4 h-4" />Certifications</h2>
          <p className="text-sm text-gray-700">{certifications.map(c => typeof c === 'string' ? c : c.name).join(', ')}</p>
        </div>
      )}

      {/* Languages */}
      {languages.length > 0 && (
        <div className="mb-4" style={getFontStyle(resume, 'languages')}>
          <h2 className="text-lg font-bold mb-1" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'languages') }}>Languages</h2>
          <p className="text-sm text-gray-700">{languages.map(l => typeof l === 'string' ? l : l.name).join(', ')}</p>
        </div>
      )}

      {/* Awards */}
      {awards.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg font-bold mb-1" style={{ color: highlightColor }}>Awards</h2>
          <p className="text-sm text-gray-700">{awards.join(', ')}</p>
        </div>
      )}
    </div>
  );
};

const ImportCustomTemplate = ({ resume }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasHtml, setHasHtml] = useState(false);

  const resumeId = resume?.id || resume?._id || resume?.resume_id;

  useEffect(() => {
    if (!resumeId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchHtml = async () => {
      setLoading(true);
      try {
        const importedHtml = await resumeAPI.fetchImportedHtml(resumeId);
        if (!cancelled && importedHtml) {
          setHtml(importedHtml);
          setHasHtml(true);
        }
      } catch {
        // No imported HTML available -- fall back to structured rendering
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHtml();
    return () => { cancelled = true; };
  }, [resumeId]);

  if (loading) {
    return (
      <ResumeBrandingWrapper resume={resume}>
        <div className="flex items-center justify-center p-8 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          Loading imported document...
        </div>
      </ResumeBrandingWrapper>
    );
  }

  if (hasHtml) {
    const globalFs = resume?.font_size_global;
    return (
      <ResumeBrandingWrapper resume={resume}>
        <div
          className="imported-docx-content prose max-w-none p-6"
          style={globalFs ? { fontSize: `${globalFs}pt` } : {}}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </ResumeBrandingWrapper>
    );
  }

  return (
    <ResumeBrandingWrapper resume={resume}>
      <StructuredFallback resume={resume} />
    </ResumeBrandingWrapper>
  );
};

export default ImportCustomTemplate;
